import { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import {
  MessageSquareText, Bot, CalendarClock, FileSpreadsheet,
  Users, BarChart3, Sparkles, ArrowLeft, CheckCircle2,
  FileText, Mail, Smartphone, HardDriveDownload, Loader2, X,
} from "lucide-react";
import { toast, Toaster } from "../components/ui/sonner";
import { api } from "../lib/api";

/**
 * "אוטומציות" Hub — central place for every cross-company automation.
 * Renamed from the previous "אוטומציות נוספות" wording to match the new
 * positioning: this is the canonical entry point to every feature, not
 * a list of upsells.
 *
 * Tiles can declare either:
 *   • `to(companyId)` — a navigation link (most cases)
 *   • `kind: "action"` + `action(companyId)` — fires custom flow when clicked
 *     (used by the "full backup" tile to launch a company picker dialog)
 */

const TILES = [
  {
    kind: "action",
    actionId: "full-backup",
    icon: HardDriveDownload,
    title: "גיבוי מלא של עסק",
    subtitle:
      "מורידה את 100% של העסק: כל מסד הנתונים + סרטון וידאו מלא של כל דף בדשבורד עם פתיחת כל החלונות. הכל נשמר בתיקייה ייעודית ב-Google Drive עם תאריך ושעה.",
    details: [
      "✓ מסד נתונים מלא (JSON) — כל הלקוחות, הצעות מחיר, הוצאות, ספקים, פעולות",
      "✓ סרטון וידאו (.webm) של הדשבורד כולו — סקירה איטית של כל דף וכל מודאל",
      "✓ נשמר ב-Google Drive בתיקייה עם תאריך ושעה ישראליים",
      "✓ ניתן לשחזור 1:1 — מחזיר את העסק בדיוק לאיך שהיה ביום הגיבוי",
      "✓ אורך גיבוי: 3-10 דקות לפי גודל העסק",
    ],
    gradient: "from-fuchsia-500 to-violet-700",
    price: null,
    active: true,
    badgeText: "כלי אדמין",
  },
  {
    to: (companyId) => `/S.ADMIN/automations/wa-broadcast${companyId ? `?company=${companyId}` : ""}`,
    icon: MessageSquareText,
    title: "דיוור הודעות WhatsApp",
    subtitle: "שליחה רחבה ללקוחות בהתבסס על תבניות מאושרות, תמיכה בסרטון / תמונה / טקסט ומספרי טלפון ממקורות שונים",
    details: [
      "✓ העלאת רשימת טלפונים מ-Excel / Google Sheets / הקלדה ידנית",
      "✓ תבניות הודעה מאושרות מראש (text, image, video)",
      "✓ דוחות מסירה לכל מספר בנפרד",
      "✓ תמיכה במשתני שם פרטי + שם משפחה אוטומטית",
    ],
    gradient: "from-emerald-500 to-teal-600",
    price: "14.99",
    active: true,
    ownedWhenChannel: null,
  },
  {
    icon: FileText,
    title: "אוטומציה למסמכים ב-WhatsApp",
    subtitle: "שליחה אוטומטית של חשבוניות, קבלות והצעות מחיר ישירות ל-WhatsApp של הלקוח, עם תבנית מאושרת ומעקב סטטוס מלא",
    details: [
      "✓ חיבור ישיר ל-Green Invoice — שליחה ברגע ההפקה",
      "✓ קישור לחשבונית PDF מצורף אוטומטית",
      "✓ דוחות הצלחה/כישלון בזמן אמת",
      "✓ ניסיון חוזר אוטומטי על כישלונות",
    ],
    gradient: "from-green-500 to-emerald-600",
    price: "14.99",
    active: true,
    to: (companyId) => `/S.ADMIN/invoices${companyId ? `?company=${companyId}` : ""}`,
    ownedWhenChannel: "whatsapp",
  },
  {
    icon: Mail,
    title: "אוטומציה למסמכים באימייל",
    subtitle: "שליחה אוטומטית של מסמכים מעוצבים דרך Brevo לכל הלקוחות, עם מעקב פתיחות, ותוכן מותאם אישית",
    details: [
      "✓ מייל מעוצב במיתוג העסק (לוגו + צבעים)",
      "✓ Brevo SMTP — איכות מסירה גבוהה",
      "✓ מעקב פתיחות + לחיצות על קישורים",
      "✓ קובץ ה-PDF מצורף + מוטמע בגוף המייל",
    ],
    gradient: "from-blue-500 to-cyan-600",
    price: "14.99",
    active: true,
    to: (companyId) => `/S.ADMIN/invoices${companyId ? `?company=${companyId}` : ""}`,
    ownedWhenChannel: "email",
  },
  {
    icon: Smartphone,
    title: "אוטומציה למסמכים ב-SMS",
    subtitle: "חלופה מהירה ליעד שאין בו WhatsApp — שליחת קישור לחשבונית דרך SMS עם תמיכת תו ישראלי מלא",
    details: [
      "✓ Twilio SMS — מסירה מיידית בישראל",
      "✓ קישור קצור לחשבונית (PDF)",
      "✓ עברית מלאה עם תווי שורה",
      "✓ דוח אספקה מ-Twilio",
    ],
    gradient: "from-amber-500 to-orange-600",
    price: "14.99",
    active: false,
    ownedWhenChannel: "sms",
  },
  {
    icon: Bot,
    title: "בוט AI לוואטסאפ",
    subtitle: "חיבור GPT שעונה ללקוחות אוטומטית על שאלות נפוצות, מזהה תשלום שהתבצע ומכווין לנציג אנושי",
    details: [
      "✓ GPT-5 לזיהוי כוונה ותשובה אוטומטית",
      "✓ זיהוי תשלום בWA והפניה לסליקה",
      "✓ הסלמה לנציג אנושי כשנדרש",
      "✓ למידה מהיסטוריית השיחות",
    ],
    gradient: "from-indigo-500 to-purple-600",
    price: "14.99",
    active: false,
  },
  {
    icon: CalendarClock,
    title: "תזכורות חכמות",
    subtitle: "לקוח לא שילם 7 ימים? תזכורת אוטומטית ב-WhatsApp + אימייל. לפי סכום, יום, או חשבונית פתוחה",
    details: [
      "✓ כללי תזמון: לפי ימים מתאריך החשבונית",
      "✓ הסלמה: WA → אימייל → SMS לפי תגובה",
      "✓ תזכורות חוזרות עד תשלום",
      "✓ סיום אוטומטי כשהחשבונית נסגרת",
    ],
    gradient: "from-rose-500 to-pink-600",
    price: "14.99",
    active: false,
  },
  {
    icon: FileSpreadsheet,
    title: "ייבוא מרובה מ-Google Sheets",
    subtitle: "לטעון מספר גיליונות במקביל, למפות טורים אוטומטית ולהתחיל שליחה מרובת חברות בלחיצה",
    details: [
      "✓ חיבור ישיר ל-Google Sheets",
      "✓ זיהוי אוטומטי של עמודות (שם, טלפון, סכום)",
      "✓ ייבוא מכמה גיליונות בו-זמנית",
      "✓ שמירת מיפוי טורים לפעם הבאה",
    ],
    gradient: "from-sky-500 to-blue-600",
    price: "14.99",
    active: false,
  },
  {
    icon: Users,
    title: "ניהול אנשי קשר חכם",
    subtitle: "בסיס נתונים מאוחד של כל הלקוחות עם היסטוריה, תיוגים ופילוח לקמפיינים",
    details: [
      "✓ פרופיל מאוחד לכל לקוח (טלפון/אימייל/היסטוריה)",
      "✓ תיוגים: VIP, חוב, פעיל, רדום",
      "✓ פילוח דינמי לקמפיינים",
      "✓ הדפסת רשימה / ייצוא ל-Excel",
    ],
    gradient: "from-fuchsia-500 to-pink-600",
    price: "14.99",
    active: false,
  },
  {
    icon: BarChart3,
    title: "לוח אנליטיקה",
    subtitle: "כמה נשלח, כמה התקבל, כמה שולם, כמה נקרא — בגרפים יפים לפי חברה / חודש",
    details: [
      "✓ KPIs מרכזיים בכרטיסים גדולים",
      "✓ גרפים: הכנסות, חשבוניות, פתיחות, תגובות",
      "✓ פילטרים: חברה / חודש / סטטוס",
      "✓ ייצוא דוח PDF",
    ],
    gradient: "from-violet-500 to-fuchsia-600",
    price: "14.99",
    active: false,
  },
];

export default function AutomationsHub() {
  const location = useLocation();
  const companyId = new URLSearchParams(location.search).get("company");
  const [company, setCompany] = useState(null);
  const [allCompanies, setAllCompanies] = useState([]);
  const [backupDialog, setBackupDialog] = useState(false);
  const [expandedTile, setExpandedTile] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/companies");
        const list = data.companies || [];
        setAllCompanies(list);
        if (companyId) setCompany(list.find((c) => c.id === companyId) || null);
      } catch { /* noop */ }
    })();
  }, [companyId]);

  const channels = company?.channels || [];

  const onTileAction = (actionId) => {
    if (actionId === "full-backup") setBackupDialog(true);
  };

  return (
    <div dir="rtl" className="p-6 sm:p-10 max-w-6xl mx-auto" data-testid="automations-hub">
      <Toaster position="top-center" richColors />

      {/* Header */}
      <div className="mb-8 flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3">
          <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-brand to-brand-hover flex items-center justify-center shadow-md">
            <Sparkles className="h-6 w-6 text-white" />
          </div>
          <div>
            <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight text-stone-900">
              כל האוטומציות
            </h1>
            <p className="text-stone-600 mt-1 text-sm">
              {company
                ? <>השירותים הזמינים עבור <b className="text-stone-900">{company.name}</b>. בסימון ירוק — מה שכבר פעיל אצלך.</>
                : "מגוון כלים חכמים שמייעלים את העסק שלך. לחצי על אריח לפרטים מלאים."}
            </p>
          </div>
        </div>
      </div>

      {/* Tiles grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {TILES.map((t, i) => {
          const Icon = t.icon;
          const owned = t.ownedWhenChannel && channels.includes(t.ownedWhenChannel);
          const resolvedTo = t.active && t.to ? (typeof t.to === "function" ? t.to(companyId) : t.to) : null;
          const isAction = t.kind === "action";
          const isExpanded = expandedTile === i;

          const content = (
            <div
              className={[
                "relative rounded-2xl p-5 transition-all duration-200 overflow-hidden h-full",
                owned
                  ? "bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-300 shadow-md cursor-pointer hover:-translate-y-1 hover:shadow-xl"
                  : t.active
                  ? "cursor-pointer hover:-translate-y-1 hover:shadow-xl shadow-md bg-white border-2 border-stone-200 hover:border-brand/40"
                  : "bg-white/60 border-2 border-stone-200 opacity-70 cursor-not-allowed",
              ].join(" ")}
              data-testid={`automation-tile-${i}`}
            >
              <div className={`absolute top-0 right-0 left-0 h-1.5 bg-gradient-to-l ${t.gradient}`} />

              {/* Price / badge top-left */}
              {t.price ? (
                <div className="absolute top-3 left-3 bg-stone-900 text-white text-[10px] font-bold px-2 py-1 rounded-full flex items-center gap-1 shadow-sm">
                  <span>+</span>
                  <span>{t.price} ₪</span>
                  <span className="opacity-70">/חודש</span>
                </div>
              ) : t.badgeText ? (
                <div className="absolute top-3 left-3 bg-fuchsia-600 text-white text-[10px] font-bold px-2 py-1 rounded-full shadow-sm">
                  {t.badgeText}
                </div>
              ) : null}

              <div className={`h-14 w-14 rounded-xl bg-gradient-to-br ${t.gradient} flex items-center justify-center shadow-lg mb-4`}>
                <Icon className="h-7 w-7 text-white" />
              </div>

              <div className="space-y-1.5">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-bold text-base text-stone-900">{t.title}</h3>
                  {owned ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-emerald-600 text-white flex items-center gap-1">
                      <CheckCircle2 className="h-3 w-3" />
                      יש לך שירות זה
                    </span>
                  ) : t.active ? (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-brand text-white">חדש</span>
                  ) : (
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-stone-200 text-stone-600">בקרוב</span>
                  )}
                </div>
                <p className="text-xs text-stone-600 leading-relaxed">{t.subtitle}</p>

                {/* Details — collapsible */}
                {t.details && t.details.length > 0 && (
                  <div className="pt-2">
                    <button
                      type="button"
                      onClick={(e) => { e.preventDefault(); e.stopPropagation(); setExpandedTile(isExpanded ? null : i); }}
                      className="text-[11px] font-bold text-stone-500 hover:text-stone-900"
                      data-testid={`automation-details-toggle-${i}`}
                    >
                      {isExpanded ? "▲ הסתר פרטים" : "▼ פרטים נוספים"}
                    </button>
                    {isExpanded && (
                      <ul className="mt-2 space-y-1 text-[11px] text-stone-700 leading-snug" data-testid={`automation-details-${i}`}>
                        {t.details.map((d, idx) => <li key={idx}>{d}</li>)}
                      </ul>
                    )}
                  </div>
                )}
              </div>

              {t.active && (
                <div className={`mt-4 flex items-center gap-1 text-xs font-bold ${owned ? "text-emerald-700" : isAction ? "text-fuchsia-700" : "text-brand"}`}>
                  <span>{isAction ? "הפעלה" : owned ? "פתחי את השירות" : "כנסי"}</span>
                  <ArrowLeft className="h-3.5 w-3.5" />
                </div>
              )}
            </div>
          );

          if (isAction && t.active) {
            return (
              <button
                key={i}
                type="button"
                onClick={() => onTileAction(t.actionId)}
                className="text-right"
                data-testid={`automation-action-${t.actionId}`}
              >
                {content}
              </button>
            );
          }
          return resolvedTo ? (
            <Link key={i} to={resolvedTo} data-testid={`automation-link-${i}`}>{content}</Link>
          ) : (
            <div key={i}>{content}</div>
          );
        })}
      </div>

      {backupDialog && (
        <BackupCompanyPicker
          companies={allCompanies}
          onClose={() => setBackupDialog(false)}
        />
      )}
    </div>
  );
}

/**
 * Modal that asks which company to back up, then drives the same
 * create_backup + record_video pipeline used by the sidebar button.
 */
function BackupCompanyPicker({ companies, onClose }) {
  const [selectedId, setSelectedId] = useState(companies[0]?.id || "");
  const [running, setRunning] = useState(false);
  const [progress, setProgress] = useState(null);

  const onRun = async () => {
    if (!selectedId) { toast.error("בחרי עסק"); return; }
    setRunning(true);
    setProgress({ phase: "snapshot", text: "יוצרת snapshot של מסד הנתונים..." });
    try {
      const { data: snap } = await api.post(
        `/fullframe/backups?company_id=${encodeURIComponent(selectedId)}`,
        {},
      );
      const subId = snap?.gdrive_subfolder_id;
      const subName = snap?.gdrive_subfolder_name;
      const routesCount = snap?.routes_to_capture?.length || 1;
      if (!subId) {
        toast.success("הגיבוי הושלם (ללא Drive)");
        onClose();
        return;
      }
      setProgress({ phase: "starting", text: "מתחילה הקלטת וידאו..." });
      try {
        await api.post(`/fullframe/backups/${snap.id}/record-video?company_id=${encodeURIComponent(selectedId)}`);
      } catch { /* ignore — already running in background */ }
      const startedAt = Date.now();
      const eta = 60 + routesCount * 90;
      const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
      let done = false;
      for (let i = 0; i < 180; i++) {
        await new Promise((r) => setTimeout(r, 5000));
        let st = {};
        try {
          const r = await api.get(`/fullframe/backups/${snap.id}/video-status?company_id=${encodeURIComponent(selectedId)}`);
          st = r.data || {};
        } catch { /* transient */ }
        const elapsed = Math.round((Date.now() - startedAt) / 1000);
        if (st.video_status === "done") {
          done = true;
          setProgress({ phase: "done", text: `הושלם — וידאו ${Math.round((st.video_size_bytes || 0) / 1024 / 1024 * 10) / 10} MB`, elapsed, eta, percent: 100, folder: subName });
          break;
        }
        if (st.video_status === "failed") {
          setProgress({ phase: "failed", text: `הקלטה נכשלה: ${st.video_error || "?"}`, elapsed, eta, percent: 100 });
          break;
        }
        const percent = Math.min(95, Math.round((elapsed / eta) * 100));
        setProgress({
          phase: "recording",
          text: `מקליטה ${routesCount} דפים — חלפו ${mmss(elapsed)}, נשארו ~${mmss(Math.max(0, eta - elapsed))}`,
          elapsed, eta, percent, folder: subName,
        });
      }
      toast.success(done ? "גיבוי הושלם · נתונים + וידאו" : "וידאו עדיין מתבצע ברקע", {
        description: subName ? `Drive: ${subName}` : undefined, duration: 7000,
      });
      if (done) onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "שגיאה");
    } finally {
      setRunning(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-md flex items-center justify-center p-6" data-testid="backup-picker-overlay">
      <div className="max-w-md w-full bg-stone-900 border border-white/10 rounded-2xl shadow-2xl p-6 text-white relative">
        {!running && (
          <button type="button" onClick={onClose} className="absolute top-3 left-3 p-1.5 text-stone-400 hover:text-white" data-testid="backup-picker-close">
            <X className="h-5 w-5" />
          </button>
        )}
        <div className="flex items-center gap-3 mb-5">
          <div className="h-12 w-12 rounded-full bg-gradient-to-br from-fuchsia-500 to-violet-700 grid place-items-center">
            <HardDriveDownload className="h-6 w-6 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-extrabold">גיבוי מלא</h3>
            <p className="text-xs text-stone-400">בחרי עסק להפקת גיבוי</p>
          </div>
        </div>

        {!running ? (
          <>
            <label className="text-[11px] font-bold uppercase tracking-widest text-stone-400 block mb-1">עסק</label>
            <select
              value={selectedId}
              onChange={(e) => setSelectedId(e.target.value)}
              className="w-full bg-stone-800 border border-white/10 rounded-lg px-3 py-2.5 text-sm font-bold mb-5"
              data-testid="backup-picker-select"
            >
              {companies.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
            <button
              type="button"
              onClick={onRun}
              className="w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-l from-fuchsia-500 to-violet-700 hover:opacity-90 text-white font-extrabold text-sm shadow-lg"
              data-testid="backup-picker-run"
            >
              <HardDriveDownload className="h-4 w-4" />
              התחילי גיבוי מלא
            </button>
            <p className="mt-3 text-[11px] text-stone-500 leading-snug">
              ⏱️ הגיבוי לוקח 3-10 דקות. במהלך זמן זה לא ניתן לבצע פעולות אחרות במערכת.
            </p>
          </>
        ) : (
          <>
            <div className="w-full h-2 rounded-full bg-stone-800 overflow-hidden mb-2">
              <div
                className="h-full bg-gradient-to-l from-fuchsia-500 to-violet-700 transition-all duration-500"
                style={{ width: `${Math.max(2, progress?.percent || 5)}%` }}
              />
            </div>
            <div className="flex items-center justify-between text-[11px] text-stone-400 mb-4 tabular-nums">
              <span>{progress?.percent || 0}%</span>
              <span>{progress?.elapsed != null && `חלף ${Math.floor(progress.elapsed / 60)}:${String(progress.elapsed % 60).padStart(2, "0")}`}</span>
            </div>
            <div className="rounded-lg bg-stone-800/60 border border-white/5 p-3 text-sm text-stone-200 flex items-center gap-2">
              {progress?.phase === "done" ? <CheckCircle2 className="h-5 w-5 text-emerald-400 shrink-0" /> : <Loader2 className="h-5 w-5 animate-spin shrink-0" />}
              <span>{progress?.text}</span>
            </div>
            {progress?.folder && (
              <p className="mt-3 text-[11px] text-stone-500" dir="ltr">📁 {progress.folder}</p>
            )}
            <p className="mt-4 text-[11px] text-amber-300/90 leading-snug text-center">
              ⚠️ אל תסגרי את החלון עד שהגיבוי יושלם
            </p>
          </>
        )}
      </div>
    </div>
  );
}
